import React, { useRef, useState } from 'react';
import { AgentProduct, RequiresAction } from '../../api/bookingAgent';
import AgentInlineDeposit from './AgentInlineDeposit';
import { CartItemState, OtpInline, ProductCardsInline } from './ChatWindow';
import {
  MAX_IMAGES,
  VOICE_PHOTO_INPUT_ID,
  readImageFiles,
} from './agentImageUtils';
import { QuickReplyOption, quickReplySendValue } from './quickReplyUtils';

const VOICE_OVERLAY_STYLES = `
  @keyframes husl-orb-breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
  @keyframes husl-orb-spin { to { transform: rotate(360deg); } }
  @keyframes husl-ring-pulse {
    0% { transform: scale(0.7); opacity: 0.55; }
    100% { transform: scale(1.9); opacity: 0; }
  }
`;

interface VoiceOverlayProps {
  isListening: boolean;
  isSpeaking: boolean;
  loading: boolean;
  isAwaitingReply?: boolean;
  isGreeting?: boolean;
  voiceError?: string | null;
  requiresAction: RequiresAction | null;
  sessionId: string | null;
  quickReplies?: Array<string | QuickReplyOption>;
  onEnd: () => void;
  onVerifyOtp: (code: string, name?: string) => Promise<boolean>;
  onDepositPaid: () => void;
  onQuickReply?: (value: string) => void;
  onSendPhotos?: (images: string[]) => Promise<boolean>;
  onAddToCart?: (product: AgentProduct) => Promise<{ success: boolean; error?: string }>;
  cartState?: Record<string, CartItemState>;
}

/**
 * Full-panel immersive voice UI (ChatGPT/Claude-style). Replaces the text chat
 * while voice mode is on: a state-driven orb plus an End control, no bubbles or
 * buttons. When the agent needs OTP or a deposit, voice is paused (by the
 * widget) and the existing inline OTP/deposit component slides up as a card
 * over the dimmed orb; once done, the orb returns and voice resumes.
 */
export default function VoiceOverlay({
  isListening,
  isSpeaking,
  loading,
  isAwaitingReply = false,
  isGreeting = false,
  voiceError = null,
  requiresAction,
  sessionId,
  quickReplies = [],
  onEnd,
  onVerifyOtp,
  onDepositPaid,
  onQuickReply,
  onSendPhotos,
  onAddToCart,
  cartState,
}: VoiceOverlayProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageError, setImageError] = useState('');
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const action = requiresAction?.type;
  const inOtp = action === 'otp_input';
  const inDeposit = action === 'deposit_payment';
  const inAction = inOtp || inDeposit;
  const products = action === 'product_recommendation' ? (requiresAction?.products ?? []) : [];
  const hasProducts = products.length > 0;
  const hasQuickReplies = quickReplies.length > 0 && !inAction && !loading && !isAwaitingReply && !pendingImages.length;

  const isThinking = loading || isAwaitingReply;
  const orbState = isSpeaking || isGreeting
    ? 'speaking'
    : isThinking
      ? 'thinking'
      : isListening
        ? 'listening'
        : 'idle';
  const status = voiceError
    ? voiceError
    : inAction
      ? (inOtp ? 'Verify your mobile to continue' : 'Secure your deposit')
      : isGreeting
        ? 'Welcome…'
        : isSpeaking
          ? 'Speaking…'
          : isThinking
            ? 'Thinking…'
            : isListening
              ? 'Listening…'
              : 'Getting ready…';

  const handlePhotoPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    event.target.value = '';
    if (!files?.length) {
      return;
    }
    setImageError('');

    const remaining = MAX_IMAGES - pendingImages.length;
    if (remaining <= 0) {
      setImageError(`You can attach up to ${MAX_IMAGES} photos.`);
      return;
    }

    const { images, error } = await readImageFiles(Array.from(files).slice(0, remaining));
    if (error) {
      setImageError(error);
    }
    if (!images.length) {
      return;
    }
    setPendingImages((prev) => [...prev, ...images].slice(0, MAX_IMAGES));
  };

  const handleRemovePendingImage = (index: number) => {
    setPendingImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    setImageError('');
  };

  const handleSendPendingPhotos = async () => {
    if (!pendingImages.length || !onSendPhotos || uploadingPhotos) {
      return;
    }
    setUploadingPhotos(true);
    setImageError('');
    try {
      const sent = await onSendPhotos(pendingImages);
      if (!sent) {
        setImageError('Could not send those photos. Try again.');
        return;
      }
      setPendingImages([]);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const atImageLimit = pendingImages.length >= MAX_IMAGES;
  const photoPickerDisabled = uploadingPhotos || inAction || loading || atImageLimit;

  return (
    <div style={{
      flex: 1, minHeight: 0, position: 'relative',
      display: 'flex', flexDirection: 'column', background: '#faf8f5',
    }}>
      <style>{VOICE_OVERLAY_STYLES}</style>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 26, padding: 24,
        opacity: inAction ? 0.25 : 1,
        filter: inAction ? 'blur(3px)' : 'none',
        transition: 'opacity 0.25s ease, filter 0.25s ease',
        pointerEvents: inAction ? 'none' : 'auto',
        minHeight: 0,
      }}>
        <div style={{ position: 'relative', width: 168, height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {(orbState === 'listening' || orbState === 'speaking') && [0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                position: 'absolute', width: 120, height: 120, borderRadius: '50%',
                background: orbState === 'speaking' ? 'rgba(45,122,74,0.30)' : 'rgba(192,57,43,0.28)',
                animation: `husl-ring-pulse 1.8s ease-out ${i * 0.6}s infinite`,
              }}
            />
          ))}
          <div style={{
            position: 'relative', width: 120, height: 120, borderRadius: '50%',
            background: orbState === 'speaking'
              ? 'radial-gradient(circle at 35% 30%, #5fae7e 0%, #2d7a4a 70%)'
              : orbState === 'listening'
                ? 'radial-gradient(circle at 35% 30%, #e07a5f 0%, #c0392b 72%)'
                : 'radial-gradient(circle at 35% 30%, #6b5f54 0%, #2d2925 72%)',
            boxShadow: '0 10px 30px rgba(45,41,37,0.28)',
            animation: orbState === 'thinking'
              ? 'husl-orb-spin 3s linear infinite'
              : 'husl-orb-breathe 3.4s ease-in-out infinite',
          }}>
            {orbState === 'thinking' && (
              <span style={{
                position: 'absolute', top: 10, left: '50%', width: 10, height: 10, marginLeft: -5,
                borderRadius: '50%', background: '#f5f2ee',
              }} />
            )}
          </div>
        </div>

        <div
          role="status"
          aria-live="polite"
          style={{
            fontSize: voiceError ? 13 : 15,
            color: voiceError ? '#c0392b' : '#6b5f54',
            fontWeight: 500,
            textAlign: 'center',
            minHeight: 22,
            maxWidth: 280,
            lineHeight: 1.4,
          }}
        >
          {status}
        </div>
      </div>

      {hasQuickReplies && (
        <div style={{
          flexShrink: 0,
          padding: '0 16px 12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'center',
        }}>
          {quickReplies.map((qr, index) => {
            const option = typeof qr === 'string' ? { display: qr, value: qr } : qr;
            return (
              <button
                key={`${option.value}-${index}`}
                type="button"
                onClick={() => onQuickReply?.(quickReplySendValue(qr))}
                style={{
                  padding: '8px 14px',
                  borderRadius: 20,
                  border: '1.5px solid #d4cec8',
                  background: '#fff',
                  color: '#3a3530',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                {option.display}
              </button>
            );
          })}
        </div>
      )}

      {inAction && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            width: '100%', maxWidth: 322, maxHeight: '84%', overflowY: 'auto',
            background: '#fff', borderRadius: 18, padding: '14px 10px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
          }}>
            {inOtp && (
              <OtpInline
                mobile={requiresAction?.mobile}
                collectName={Boolean(requiresAction?.collect_name)}
                onVerify={onVerifyOtp}
              />
            )}
            {inDeposit && sessionId && (
              <AgentInlineDeposit
                sessionId={sessionId}
                amount={requiresAction?.amount || '0.00'}
                currency={(requiresAction?.currency || 'AUD').toUpperCase()}
                onPaid={onDepositPaid}
              />
            )}
          </div>
        </div>
      )}

      {hasProducts && !inAction && (
        <div style={{
          maxHeight: '45%', overflowY: 'auto', flexShrink: 0,
          padding: '0 16px 10px',
        }}>
          <ProductCardsInline
            products={products}
            onAddToCart={onAddToCart}
            cartState={cartState}
            containerStyle={{ margin: 0 }}
          />
        </div>
      )}

      {(imageError || uploadingPhotos) && (
        <div style={{
          textAlign: 'center',
          fontSize: 12,
          color: imageError ? '#c0392b' : '#6b5f54',
          padding: '0 16px 8px',
        }}>
          {uploadingPhotos ? 'Sending photos…' : imageError}
        </div>
      )}

      {pendingImages.length > 0 && (
        <div style={{
          flexShrink: 0,
          padding: '0 16px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {pendingImages.map((image, index) => (
              <div key={`${index}-${image.slice(0, 24)}`} style={{ position: 'relative' }}>
                <img
                  src={image}
                  alt={`Selected hair photo ${index + 1}`}
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10, border: '1px solid #d4cec8' }}
                />
                <button
                  type="button"
                  onClick={() => handleRemovePendingImage(index)}
                  aria-label={`Remove photo ${index + 1}`}
                  disabled={uploadingPhotos}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    background: '#2d2925',
                    color: '#fff',
                    fontSize: 14,
                    lineHeight: 1,
                    cursor: uploadingPhotos ? 'default' : 'pointer',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { void handleSendPendingPhotos(); }}
            disabled={uploadingPhotos || loading}
            style={{
              padding: '8px 18px',
              borderRadius: 20,
              border: 'none',
              background: uploadingPhotos || loading ? '#c8c1ba' : '#2d2925',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: uploadingPhotos || loading ? 'default' : 'pointer',
            }}
          >
            {uploadingPhotos ? 'Sending…' : `Send ${pendingImages.length} photo${pendingImages.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      <div style={{
        padding: '0 0 22px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 18,
        flexShrink: 0,
      }}>
        {onSendPhotos && (
          <>
            <input
              ref={fileInputRef}
              id={VOICE_PHOTO_INPUT_ID}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              style={{ display: 'none' }}
              onChange={handlePhotoPick}
              disabled={photoPickerDisabled}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label={atImageLimit ? `Photo limit reached (${MAX_IMAGES})` : 'Add photos of your hair'}
              title={atImageLimit ? `You can attach up to ${MAX_IMAGES} photos` : 'Add photos of your hair'}
              disabled={photoPickerDisabled}
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: '1.5px solid #d4cec8',
                background: '#fff',
                color: '#3a3530',
                cursor: photoPickerDisabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: photoPickerDisabled ? 0.45 : 1,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 4h6l2 2h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3l2-2z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </button>
          </>
        )}
        <button
          onClick={onEnd}
          aria-label="End voice mode"
          title="End voice"
          style={{
            width: 56, height: 56, borderRadius: '50%', border: 'none',
            background: '#2d2925', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="5" y="5" width="10" height="10" rx="2" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
}
